import React, { useEffect, useState } from "react";
import { Button, Spinner, Notification, toast } from "components/ui";
import { AdaptableCard, ConfirmDialog } from "components/shared";
import { useNavigate } from "react-router-dom";
import AccountService from "views/v2/services/accountService";
import { ImSpinner9 } from "react-icons/im";
import { useChildWindow } from "views/v2/utils/helperChildWindow";
import SmtpModal from "./smtpModal";

const Integration = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { open } = useChildWindow({ title: "Integration Status" });

  const [loading, setLoading] = useState(false);
  const [configuredIntegrations, setConfiguredIntegrations] = useState({});
  const [successfulIntegrations, setSuccessfulIntegrations] = useState(() => {
    const saved = localStorage.getItem("successfulIntegrations");
    return saved ? JSON.parse(saved) : [];
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentIntegrationId, setCurrentIntegrationId] = useState(null);

  const integrations = [
    // {
    //   key: "GMAIL",
    //   name: "Gmail",
    //   img: (
    //     <img
    //       src="/images/gmail.png"
    //       alt="Gmail"
    //       className="h-16 object-contain bg-transparent"
    //     />
    //   ),
    //   description:
    //     "Gmail integration for sending and receiving emails, with seamless connection to Google Workspace apps.",
    //   beta: true,
    // },
    {
      key: "O365",
      name: "O365",
      img: (
        <img
          src="/images/office365.png"
          alt="O365"
          className="h-14 object-contain bg-transparent"
        />
      ),
      description:
        "Microsoft’s cloud-based email and productivity suite with seamless integration for business collaboration.",
      beta: true,
    },
    {
      key: "SMTP",
      name: "SMTP",
      img: (
        <img
          src="/images/smtp.png"
          alt="smtp"
          className="h-12 object-contain bg-transparent"
        />
      ),
      description:
        "SMTP integration for sending emails securely from your applications with reliable delivery and configuration.",
      beta: true,
    },
  ];

  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      const response = await AccountService.getEmailIntegrations();
      const data = response.data.data;

      if (data && data.rows.length > 0) {
        const latestPerProvider = {};
        data.rows.forEach((row) => {
          const providerKey = row.provider?.trim();
          if (
            !latestPerProvider[providerKey] ||
            new Date(row.createdAt) >
            new Date(latestPerProvider[providerKey].createdAt)
          ) {
            latestPerProvider[providerKey] = { ...row, provider: providerKey };
          }
        });
        setConfiguredIntegrations(latestPerProvider);
      }
    } catch (error) {
      console.error("Failed to fetch integrations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);
  const postLogin = async (type, payload) => {
    setLoading(true);
    try {
      const response = await AccountService.postAccountLoginUrlByProvider(
        type,
        {
          code: payload.code,
        }
      );
      console.log("API Response:", response.data);
      toast.push(
        <Notification title="Success" type="success" closable>
          {type} integration connected successfully!
        </Notification>
      );
      fetchIntegrations();
    } catch (err) {
      console.error("API call failed:", err.response || err.message);
      toast.push(
        <Notification title="Error" type="danger" closable>
          Failed to connect {type} integration. Please try again.
        </Notification>
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {
      const { type, payload } = event.data || {};
      if (type === "GMAIL" || type === "O365") {
        postLogin(type, payload);
      }

      if (
        type === "GMAIL_INTEGRATION_SUCCESS" ||
        type === "O365_INTEGRATION_SUCCESS"
      ) {
        const key = type.includes("GMAIL") ? "GMAIL" : "O365";
        setSuccessfulIntegrations((prev) => {
          const updated = prev.includes(key) ? prev : [...prev, key];
          localStorage.setItem(
            "successfulIntegrations",
            JSON.stringify(updated)
          );
          return updated;
        });
      }

      if (
        type === "GMAIL_INTEGRATION_ERROR" ||
        type === "O365_INTEGRATION_ERROR"
      ) {
        const name = type.includes("GMAIL") ? "Gmail" : "O365";
        alert(`${name} integration failed. Please try again.`);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const handleConnect = async (key) => {
    if (key === "SMTP") {
      const id = configuredIntegrations["SMTP"]?.id || "";
      setCurrentIntegrationId(id);
      setIsModalOpen(true);
      return;
    }

    try {
      setLoading(true);

      const response = await AccountService.getLoginUrlByProvider(key);
      const url = response?.data?.data?.url;

      if (!url) {
        alert("No login URL returned from server. Please check the backend.");
        return;
      }

      open(key, url);
    } catch (error) {
      console.error("Failed to get login URL:", error);
      alert("Failed to initiate integration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async (type) => {
    setLoading(true);

    try {
      const response = await AccountService.testEmailIntegration(type);

      const { status, message } = response?.data || {};

      if (status === "SUCCESS") {
        toast.push(
          <Notification title="Success" type="success" closable>
            Integration test successful!
          </Notification>
        );
      } else {
        toast.push(
          <Notification title="Error" type="danger" closable>
            {message || "Integration test failed. Please check configuration."}
          </Notification>
        );
      }
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        "Integration test failed. Please check configuration.";

      console.error("Integration test failed:", error);

      toast.push(
        <Notification title="Error" type="danger" closable>
          {apiMessage}
        </Notification>
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setIsDeleteDialogOpen(false);
      const id = deleteTarget?.id;
      if (!id) return;

      await AccountService.deleteEmailIntegrations(id);
      toast.push(
        <Notification title="Success" type="success" closable>
          Mail integration deleted successfully!
        </Notification>
      );

      setConfiguredIntegrations((prev) => {
        const copy = { ...prev };
        delete copy[deleteTarget.provider];
        return copy;
      });

      if (["GMAIL", "O365"].includes(deleteTarget.provider)) {
        setSuccessfulIntegrations((prev) => {
          const updated = prev.filter((k) => k !== deleteTarget.provider);
          localStorage.setItem(
            "successfulIntegrations",
            JSON.stringify(updated)
          );
          return updated;
        });
      }
    } catch (error) {
      console.error("Failed to delete integration:", error);
      toast.push(
        <Notification title="Error" type="danger" closable>
          Failed to delete integration. Please try again.
        </Notification>
      );
    } finally {
      setLoading(false);
      setIsDeleteDialogOpen(false);
    }
  };

  const isAnyIntegrationEnabled =
    Object.keys(configuredIntegrations).length > 0 ||
    successfulIntegrations.length > 0;

  const content = (
      <div className={embedded ? "" : "p-6"}>
        <p className="c-h3 mb-6 text-pink-500">
          Email Integrations
        </p>

        {loading ? (
          <div className="grid justify-items-center items-center">
            <Spinner size={40} indicator={ImSpinner9} />
          </div>
        ) : (
          <div className="grid grid-cols-3 xs:grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-6 w-full text-sm">
            {integrations.map((integration, idx) => {
              const configured = configuredIntegrations[integration.key];
              const isEnabled =
                !!configured ||
                successfulIntegrations.includes(integration.key);

              return (
                <div
                  key={idx}
                  className="bg-gray-100 dark:bg-[#161616] border border-gray-700 shadow-lg rounded-lg flex flex-col justify-between min-h-[240px] hover:shadow-md transition-shadow duration-200"
                >
                  <div className="relative flex justify-center items-center mb-4 h-32 rounded-tl-lg rounded-tr-lg bg-white">
                    {integration.img}
                    {integration.beta && (
                      <span className="absolute top-4 right-4 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500 text-white">
                        Beta
                      </span>
                    )}
                  </div>

                  <div className="p-3">
                    <p className="text-black dark:text-gray-300 text-center">
                      {integration.description}
                    </p>
                    {configured?.info && (
                      <div className="mt-2 text-center text-gray-200 text-lg">
                        <div>
                          {configured.provider === "GMAIL"
                            ? configured.info?.name
                            : configured.info?.displayName}
                        </div>
                        <div className="text-lg">
                          {configured.provider === "GMAIL"
                            ? configured.info?.email
                            : configured.info?.mail}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex justify-center gap-2 xs:flex-col">
                      {isEnabled ? (
                        <>
                          {/* Delete button */}
                          <Button
                            onClick={() => {
                              setDeleteTarget(configured);
                              setIsDeleteDialogOpen(true);
                            }}
                            variant="solid"
                            color="red-600"
                            className="flex justify-center items-center flex-shrink w-auto"
                          >
                            Delete
                          </Button>

                          {configured && (
                            <Button
                              onClick={() => handleTest(integration.key)}
                              variant="solid"
                              className="flex justify-center items-center flex-shrink w-auto"
                            >
                              Test
                            </Button>
                          )}
                          {/* Info button for SMTP */}
                          {integration.key === "SMTP" && configured && (
                            <Button
                              onClick={() => {
                                setCurrentIntegrationId(configured.id);
                                setIsModalOpen(true);
                              }}
                              variant="solid"
                              className="flex justify-center items-center flex-shrink w-auto"
                            >
                              Info
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          variant="solid"
                          onClick={() => handleConnect(integration.key)}
                          disabled={isAnyIntegrationEnabled}
                          className="flex justify-center items-center flex-shrink w-auto"
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <ConfirmDialog
          closable={false}
          title="Are you sure you want to delete?"
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onRequestClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDelete}
          onCancel={() => setIsDeleteDialogOpen(false)}
          type="warning"
        />

        <SmtpModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          integrationId={currentIntegrationId}
          onSaveSuccess={fetchIntegrations}
        />
      </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <AdaptableCard className="min-h-screen w-full" divider>
      {content}
    </AdaptableCard>
  );
};

export default Integration;
